import { createSlice } from "@reduxjs/toolkit";
import {
    createBaseNavigation,
    deleteBaseNavigation,
    getBaseNavigationDropdownList,
    getBaseNavigations,
    updateBaseNavigation,
} from "./baseNavigationThunk";

// ==========================================
// CACHE CONFIG
// ==========================================
export const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min — import this in the thunk to decide `fromCache`
const MAX_CACHE_ENTRIES = 30; // cap distinct filter/page/search combos kept in memory
const MAX_DROPDOWN_CACHE_ENTRIES = 10;

const initialState = {
    loading: false,
    dropdownLoading: false,

    baseNavigationlist: [],
    baseNavigationlistDropdownList: [],

    pagination: {},
    error: null,
    cache: {}, // keyed by JSON.stringify(payload) -> { data, pagination, timestamp }
    dropdownCache: {},
};

// Evicts the oldest entries (by timestamp) once a cache map exceeds maxEntries.
// Mutates in place — safe under Immer inside a reducer.
const pruneCache = (cacheObj, maxEntries) => {
    const keys = Object.keys(cacheObj);
    if (keys.length <= maxEntries) return;

    const sortedByAge = keys.sort(
        (a, b) => cacheObj[a].timestamp - cacheObj[b].timestamp,
    );

    const toRemove = sortedByAge.slice(0, keys.length - maxEntries);
    toRemove.forEach((key) => delete cacheObj[key]);
};

const baseNavigationSlice = createSlice({
    name: "baseNavigation",
    initialState,
    reducers: {
        // ==========================================
        // CLEAR ALL BASE NAVIGATION CACHE
        // ==========================================
        clearBaseNavigationCache: (state) => {
            state.cache = {};
            state.dropdownCache = {};
        },

        // ==========================================
        // CLEAR SPECIFIC LIST CACHE
        // ==========================================
        clearBaseNavigationCacheByKey: (state, action) => {
            delete state.cache[action.payload];
        },

        // ==========================================
        // CLEAR ONLY DROPDOWN CACHE
        // ==========================================
        clearBaseNavigationDropdownCache: (state) => {
            state.dropdownCache = {};
        },

        // ==========================================
        // RESET LIST (no cache lookup) — e.g. filters cleared,
        // nothing selected, table should show empty state
        // ==========================================
        resetBaseNavigationList: (state) => {
            state.baseNavigationlist = [];
            state.pagination = {};
            state.loading = false;
            state.error = null;
        },
    },

    extraReducers: (builder) => {
        builder
            /* ==========================================
               GET BASE NAVIGATIONS
            ========================================== */
            .addCase(getBaseNavigations.pending, (state) => {
                state.loading = true;
                state.error = null;
            })

            .addCase(getBaseNavigations.fulfilled, (state, action) => {
                state.loading = false;
                state.baseNavigationlist = action.payload.data;
                state.pagination = action.payload.pagination;

                if (!action.payload.fromCache) {
                    state.cache[action.payload.cacheKey] = {
                        data: action.payload.data,
                        pagination: action.payload.pagination,
                        timestamp: Date.now(),
                    };

                    pruneCache(state.cache, MAX_CACHE_ENTRIES);
                }
            })

            .addCase(getBaseNavigations.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ==========================================
               CREATE BASE NAVIGATION
            ========================================== */
            .addCase(createBaseNavigation.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createBaseNavigation.fulfilled, (state) => {
                state.loading = false;
                state.cache = {};
                state.dropdownCache = {};
            })
            .addCase(createBaseNavigation.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ==========================================
               UPDATE BASE NAVIGATION
            ========================================== */
            .addCase(updateBaseNavigation.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateBaseNavigation.fulfilled, (state) => {
                state.loading = false;
                state.cache = {};
                state.dropdownCache = {};
            })
            .addCase(updateBaseNavigation.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ==========================================
               DELETE BASE NAVIGATION
            ========================================== */
            .addCase(deleteBaseNavigation.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteBaseNavigation.fulfilled, (state) => {
                state.loading = false;
                state.cache = {};
                state.dropdownCache = {};
            })
            .addCase(deleteBaseNavigation.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            /* ==========================================
               GET DROPDOWN LIST
            ========================================== */
            .addCase(getBaseNavigationDropdownList.pending, (state) => {
                state.dropdownLoading = true;
                state.error = null;
            })
            .addCase(getBaseNavigationDropdownList.fulfilled, (state, action) => {
                state.dropdownLoading = false;

                state.baseNavigationlistDropdownList = action.payload.data;

                if (!action.payload.fromCache) {
                    state.dropdownCache[action.payload.cacheKey] = {
                        data: action.payload.data,
                        timestamp: Date.now(),
                    };

                    pruneCache(state.dropdownCache, MAX_DROPDOWN_CACHE_ENTRIES);
                }
            })
            .addCase(getBaseNavigationDropdownList.rejected, (state, action) => {
                state.dropdownLoading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearBaseNavigationCache,
    clearBaseNavigationCacheByKey,
    clearBaseNavigationDropdownCache,
    resetBaseNavigationList,
} = baseNavigationSlice.actions;

export default baseNavigationSlice.reducer;